/**
 * MedPass Workflow Component
 * 
 * Main component for the 4-step MedPass electronic prescription workflow:
 * 1. Patient Demographics
 * 2. Medication Order Entry  
 * 3. Pre/Post Conditions
 * 4. Physician Notes & Scheduling
 */

import React, { useState, useEffect } from 'react';
import {
    Steps,
    Card,
    Button,
    message,
    Spin,
    Progress,
    Alert,
    Modal,
    Badge,
    Descriptions,
    Row,
    Col,
    Space,
    Tag,
    Tooltip,
    Divider
} from 'antd';
import {
    UserOutlined,
    MedicineBoxOutlined,
    HeartOutlined,
    FileTextOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    SafetyCertificateOutlined,
    ClockCircleOutlined
} from '@ant-design/icons';

import medpassService from '../../services/medpassService';
import PatientDemographicsStep from './MedPassSteps/PatientDemographicsStep';
import MedicationOrderEntryStep from './MedPassSteps/MedicationOrderEntryStep';
import PrePostConditionsStep from './MedPassSteps/PrePostConditionsStep';
import PhysicianNotesSchedulingStep from './MedPassSteps/PhysicianNotesSchedulingStep';
import MedPassSafetyAlerts from './MedPassComponents/MedPassSafetyAlerts';
import MedPassValidationErrors from './MedPassComponents/MedPassValidationErrors';

const { Step } = Steps;

const MedPassWorkflow = ({ workflowId, onWorkflowComplete, onCancel, mode = 'create' }) => {
    // State management
    const [loading, setLoading] = useState(false);
    const [workflow, setWorkflow] = useState(null);
    const [currentStep, setCurrentStep] = useState(0);
    const [validationResult, setValidationResult] = useState(null);
    const [safetyAlerts, setSafetyAlerts] = useState([]);
    const [showSafetyModal, setShowSafetyModal] = useState(false);
    const [showValidationModal, setShowValidationModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Step definitions
    const steps = [
        {
            title: 'Patient Demographics',
            key: 'patient_demographics',
            icon: <UserOutlined />,
            description: 'Verify patient information and medical history'
        },
        {
            title: 'Medication Order Entry',
            key: 'medication_order_entry',
            icon: <MedicineBoxOutlined />,
            description: 'Select medications and dosing instructions'
        },
        {
            title: 'Pre/Post Conditions',
            key: 'pre_post_conditions',
            icon: <HeartOutlined />,
            description: 'Define monitoring requirements and vital signs'
        },
        {
            title: 'Physician Notes & Scheduling',
            key: 'physician_notes_scheduling',
            icon: <FileTextOutlined />,
            description: 'Add clinical documentation and medication schedule'
        }
    ];

    // Load workflow on mount
    useEffect(() => {
        if (workflowId && mode !== 'create') {
            loadWorkflow();
        } else if (mode === 'create') {
            initializeNewWorkflow();
        }
    }, [workflowId, mode]);

    // Map workflow step to component step index
    useEffect(() => {
        if (workflow) {
            const stepMapping = {
                'patient_demographics': 0,
                'medication_order_entry': 1,
                'pre_post_conditions': 2,
                'physician_notes_scheduling': 3
            };
            setCurrentStep(stepMapping[workflow.current_step] || 0);
        }
    }, [workflow]);

    const initializeNewWorkflow = () => {
        setWorkflow({
            workflow_id: null,
            current_step: 'patient_demographics',
            status: 'draft',
            progress_percentage: 0,
            demographics_completed: false,
            medication_entry_completed: false,
            conditions_completed: false,
            clinical_documentation_completed: false,
            patient_profile: null,
            medications: [],
            pre_conditions: [],
            post_conditions: [],
            physician_notes: [],
            scheduling_info: null,
            safety_alerts: [],
            validation_errors: [],
            requires_physician_approval: true,
            submitted_for_approval: false
        });
    };

    const loadWorkflow = async () => {
        setLoading(true);
        try {
            const result = await medpassService.getWorkflowById(workflowId);
            if (result.success) {
                setWorkflow(result.data);
            } else {
                message.error(result.message);
            }
        } catch (error) {
            message.error('Failed to load workflow');
            console.error('Error loading workflow:', error);
        } finally {
            setLoading(false);
        }
    };

    const saveWorkflow = async (stepData, stepKey) => {
        setLoading(true);
        try {
            let updateData;
            
            // Handle different step data structures
            if (stepKey === 'medications') {
                // For medications step, stepData already contains the full structure:
                // { medications: [...], drug_interactions_checked: bool, allergies_checked: bool, contraindications_checked: bool }
                updateData = stepData;
            } else {
                // For other steps, wrap in step key
                updateData = {
                    [stepKey]: stepData
                };
            }

            let result;
            if (workflow.workflow_id) {
                // Update existing workflow
                result = await medpassService.updateWorkflow(workflow.workflow_id, updateData);
            } else {
                // Create new workflow
                const workflowData = {
                    patient_profile: stepData,
                    prescription_id: null
                };
                result = await medpassService.createWorkflow(workflowData);
            }

            if (result.success) {
                setWorkflow(result.data);
                message.success('Step saved successfully');
                return true;
            } else {
                message.error(result.message);
                return false;
            }
        } catch (error) {
            message.error('Failed to save step');
            console.error('Error saving step:', error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const completeStep = async (stepKey) => {
        if (!workflow.workflow_id) {
            message.error('Please save the current step first');
            return false;
        }

        setLoading(true);
        try {
            const completionData = {
                step: stepKey,
                completed_by: 'current_user', // This should come from auth context
                notes: null
            };

            const result = await medpassService.completeStep(workflow.workflow_id, completionData);
            if (result.success) {
                setWorkflow(result.data);
                message.success('Step completed successfully');
                return true;
            } else {
                message.error(result.message);
                return false;
            }
        } catch (error) {
            message.error('Failed to complete step');
            console.error('Error completing step:', error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const runSafetyChecks = async () => {
        if (!workflow.workflow_id) {
            message.error('Please save the workflow first');
            return;
        }

        setLoading(true);
        try {
            const result = await medpassService.runSafetyChecks(workflow.workflow_id);
            if (result.success) {
                setSafetyAlerts(result.data);
                if (result.data.length > 0) {
                    setShowSafetyModal(true);
                } else {
                    message.success('No safety issues found');
                }
            } else {
                message.error(result.message);
            }
        } catch (error) {
            message.error('Failed to run safety checks');
            console.error('Error running safety checks:', error);
        } finally {
            setLoading(false);
        }
    };

    const validateWorkflow = async () => {
        if (!workflow.workflow_id) {
            message.error('Please save the workflow first');
            return;
        }

        setLoading(true);
        try {
            const result = await medpassService.validateWorkflow(workflow.workflow_id);
            if (result.success) {
                setValidationResult(result.data);
                if (!result.data.is_valid) {
                    setShowValidationModal(true);
                } else {
                    message.success('Workflow validation passed');
                }
            } else {
                message.error(result.message);
            }
        } catch (error) {
            message.error('Failed to validate workflow');
            console.error('Error validating workflow:', error);
        } finally {
            setLoading(false);
        }
    };

    const submitForApproval = async () => {
        if (!medpassService.canSubmitForApproval(workflow)) {
            message.error('Workflow is not ready for approval');
            return;
        }

        setIsSubmitting(true);
        try {
            const approvalData = {
                submitted_by: 'current_user', // This should come from auth context
                approval_notes: null
            };

            const result = await medpassService.submitForApproval(workflow.workflow_id, approvalData);
            if (result.success) {
                setWorkflow(result.data);
                message.success('Workflow submitted for approval');
                if (onWorkflowComplete) {
                    onWorkflowComplete(result.data);
                }
            } else {
                message.error(result.message);
            }
        } catch (error) {
            message.error('Failed to submit for approval');
            console.error('Error submitting for approval:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const nextStep = async () => {
        // Check if workflow has an ID (has been saved)
        if (!workflow?.workflow_id) {
            message.error('Please save the current step first');
            return false;
        }
        
        // For now, just advance to the next step since the save already completed the step data
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
            // Mark the step as completed in the workflow
            const stepKey = steps[currentStep].key;
            await markStepCompleted(stepKey);
        }
        return true;
    };

    const prevStep = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const markStepCompleted = async (stepKey) => {
        try {
            const completionData = {
                step: stepKey,
                completed_by: 'current_user', // This should come from auth context
                notes: null
            };

            const result = await medpassService.completeStep(workflow.workflow_id, completionData);
            if (result.success) {
                setWorkflow(result.data);
                message.success('Step completed successfully');
                return true;
            } else {
                message.error(result.message);
                return false;
            }
        } catch (error) {
            message.error('Failed to complete step');
            console.error('Error completing step:', error);
            return false;
        }
    };

    const getStepStatus = (stepKey, index) => {
        if (!workflow) return 'wait';
        
        // Map step keys to completion flags
        const completionFlags = {
            'patient_demographics': workflow.demographics_completed,
            'medication_order_entry': workflow.medication_entry_completed,
            'pre_post_conditions': workflow.conditions_completed,
            'physician_notes_scheduling': workflow.clinical_documentation_completed
        };
        
        if (completionFlags[stepKey]) {
            return 'finish';
        } else if (index === currentStep) {
            return 'process';
        } else {
            return 'wait';
        }
    };

    const renderCurrentStep = () => {
        if (!workflow) return null;

        const stepProps = {
            workflow,
            onSave: saveWorkflow,
            onNext: nextStep,
            onPrev: prevStep,
            loading
        };

        switch (currentStep) {
            case 0:
                return <PatientDemographicsStep {...stepProps} />;
            case 1:
                return <MedicationOrderEntryStep {...stepProps} />;
            case 2:
                return <PrePostConditionsStep {...stepProps} />;
            case 3:
                return <PhysicianNotesSchedulingStep {...stepProps} />;
            default:
                return null;
        }
    };

    const renderWorkflowHeader = () => {
        if (!workflow) return null;

        const stepNames = medpassService.getWorkflowStepNames();
        const statusNames = medpassService.getWorkflowStatusNames();
        const progress = medpassService.calculateProgress(workflow);

        return (
            <Card className="medpass-workflow-header" style={{ marginBottom: 16 }}>
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={12} md={8}>
                        <Descriptions size="small" column={1}>
                            <Descriptions.Item label="Patient">
                                {workflow.patient_profile?.full_name || 'Not specified'}
                            </Descriptions.Item>
                            <Descriptions.Item label="MRN">
                                {workflow.patient_profile?.mrn || 'Not specified'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Status">
                                <Tag color={workflow.status === 'approved' ? 'green' : 'blue'}>
                                    {statusNames[workflow.status] || workflow.status}
                                </Tag>
                            </Descriptions.Item>
                        </Descriptions>
                    </Col>
                    
                    <Col xs={24} sm={12} md={8}>
                        <div style={{ textAlign: 'center' }}>
                            <Progress
                                type="circle"
                                percent={progress}
                                size={80}
                                status={progress === 100 ? 'success' : 'active'}
                            />
                            <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                                Overall Progress
                            </div>
                        </div>
                    </Col>

                    <Col xs={24} sm={24} md={8}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            {workflow.safety_alerts?.length > 0 && (
                                <Alert
                                    message={`${workflow.safety_alerts.length} Safety Alert(s)`}
                                    type="warning"
                                    size="small"
                                    showIcon
                                    action={
                                        <Button size="small" onClick={() => setShowSafetyModal(true)}>
                                            View
                                        </Button>
                                    }
                                />
                            )}
                            
                            {workflow.validation_errors?.length > 0 && (
                                <Alert
                                    message={`${workflow.validation_errors.length} Validation Error(s)`}
                                    type="error"
                                    size="small"
                                    showIcon
                                    action={
                                        <Button size="small" onClick={() => setShowValidationModal(true)}>
                                            View
                                        </Button>
                                    }
                                />
                            )}
                        </Space>
                    </Col>
                </Row>
            </Card>
        );
    };

    const renderActionButtons = () => {
        if (!workflow) return null;

        const isLastStep = currentStep === steps.length - 1;
        const canSubmit = medpassService.canSubmitForApproval(workflow);

        return (
            <Card style={{ marginTop: 16 }}>
                <Row justify="space-between" align="middle">
                    <Col>
                        <Space>
                            <Button onClick={onCancel}>
                                Cancel
                            </Button>
                            
                            <Button 
                                onClick={runSafetyChecks}
                                icon={<SafetyCertificateOutlined />}
                                loading={loading}
                            >
                                Safety Check
                            </Button>
                            
                            <Button 
                                onClick={validateWorkflow}
                                icon={<ExclamationCircleOutlined />}
                                loading={loading}
                            >
                                Validate
                            </Button>
                        </Space>
                    </Col>

                    <Col>
                        <Space>
                            {currentStep > 0 && (
                                <Button onClick={prevStep}>
                                    Previous
                                </Button>
                            )}
                            
                            {!isLastStep && (
                                <Button type="primary" onClick={nextStep} loading={loading}>
                                    Next
                                </Button>
                            )}
                            
                            {isLastStep && canSubmit && (
                                <Button 
                                    type="primary" 
                                    onClick={submitForApproval}
                                    loading={isSubmitting}
                                    icon={<CheckCircleOutlined />}
                                >
                                    Submit for Approval
                                </Button>
                            )}
                        </Space>
                    </Col>
                </Row>
            </Card>
        );
    };

    if (loading && !workflow) {
        return (
            <Card style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>Loading MedPass workflow...</div>
            </Card>
        );
    }

    return (
        <div className="medpass-workflow">
            {renderWorkflowHeader()}
            
            {/* Progress Steps */}
            <Card style={{ marginBottom: 16 }}>
                <Steps 
                    current={currentStep} 
                    size="small"
                    responsive={false}
                >
                    {steps.map((step, index) => (
                        <Step
                            key={step.key}
                            title={step.title}
                            description={step.description}
                            icon={step.icon}
                            status={
                                workflow ? getStepStatus(step.key, index) : 'wait'
                            }
                        />
                    ))}
                </Steps>
            </Card>

            {/* Current Step Content */}
            <div className="medpass-step-content">
                {renderCurrentStep()}
            </div>

            {/* Action Buttons */}
            {renderActionButtons()}

            {/* Safety Alerts Modal */}
            <Modal
                title="Safety Alerts"
                open={showSafetyModal}
                onCancel={() => setShowSafetyModal(false)}
                footer={[
                    <Button key="close" onClick={() => setShowSafetyModal(false)}>
                        Close
                    </Button>
                ]}
                width={800}
            >
                <MedPassSafetyAlerts alerts={safetyAlerts} />
            </Modal>

            {/* Validation Errors Modal */}
            <Modal
                title="Validation Errors"
                open={showValidationModal}
                onCancel={() => setShowValidationModal(false)}
                footer={[
                    <Button key="close" onClick={() => setShowValidationModal(false)}>
                        Close
                    </Button>
                ]}
                width={600}
            >
                <MedPassValidationErrors validation={validationResult} />
            </Modal>
        </div>
    );
};

export default MedPassWorkflow;